import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { errMsg } from "../lib/api";
import { useToast } from "../lib/toast";
import AddFileModal from "../components/AddFileModal";
import NaqoodInfoModal from "../components/NaqoodInfoModal";
import { AppShell, Sidebar, Topbar } from "../components/Layout";
import {
  Button,
  Card,
  EmptyState,
  Skeleton,
  cx,
} from "../components/ui";
import {
  PlusIcon,
  FileIcon,
  LinkIcon,
  CheckIcon,
  SettingsIcon,
  SearchIcon,
  ChevronRightIcon,
  TrashIcon,
} from "../components/ui/Icons";

export default function Dashboard() {
  const [user, setUser] = useState(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [infoFile, setInfoFile] = useState(null);

  const navigate = useNavigate();
  const toast = useToast();

  useEffect(() => {
    if (!localStorage.getItem("token")) {
      navigate("/");
      return;
    }
    Promise.all([api.get("/api/auth/me"), api.get("/api/files")])
      .then(([u, f]) => {
        setUser(u.data);
        setFiles(f.data);
      })
      .catch(() => {
        localStorage.clear();
        navigate("/");
      })
      .finally(() => setLoading(false));
  }, [navigate]);

  const saveFile = async (data) => {
    try {
      const res = await api.post("/api/files", data);
      setFiles((prev) => [res.data, ...prev]);
      setShowAdd(false);
      toast.success("File created", `"${res.data.fileName}" is ready to connect.`);
    } catch (err) {
      toast.error("Could not create file", errMsg(err));
    }
  };

  const deleteFile = async (file) => {
    const ok = window.confirm(
      `Delete "${file.fileName}"?\n\n` +
        "This removes the file and everything under it — uploads, mappings and " +
        "push history — from this app.\n\n" +
        "Records already pushed to Naqood are NOT removed."
    );
    if (!ok) return;
    try {
      await api.delete(`/api/files/${file._id}`);
      setFiles((prev) => prev.filter((f) => f._id !== file._id));
      toast.success("File deleted", `"${file.fileName}" was removed.`);
    } catch (err) {
      toast.error("Could not delete", errMsg(err));
    }
  };

  const connectNaqood = async (fileId) => {
    try {
      const res = await api.get(`/api/connect/${fileId}`);
      window.open(res.data.url, "_blank", "width=1200,height=760");
    } catch (err) {
      toast.error("Could not start connection", errMsg(err));
    }
  };


  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return files;
    return files.filter(
      (f) =>
        f.fileName?.toLowerCase().includes(q) ||
        f.naqood?.organizationSlug?.toLowerCase().includes(q)
    );
  }, [files, query]);

  const connectedCount = files.filter((f) => f.naqood?.isConnected).length;

  return (
    <AppShell sidebar={<Sidebar user={user} active="files" />}>
      <Topbar
        title="Your Files"
        subtitle={
          loading
            ? "Loading…"
            : `${files.length} file${files.length === 1 ? "" : "s"} · ${connectedCount} connected`
        }
        actions={
          <>
            <div className="relative hidden sm:block">
              <SearchIcon
                size={15}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-400"
              />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search files…"
                className="field-input h-10 w-56 !py-0 pl-9 text-[13px]"
              />
            </div>
            <Button icon={PlusIcon} onClick={() => setShowAdd(true)}>
              New File
            </Button>
          </>
        }
      />

      <main className="flex-1 bg-mesh-light px-8 py-7">
        {loading ? (
          <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
            {[0, 1, 2].map((i) => (
              <Card key={i} className="p-5">
                <Skeleton className="h-10 w-10 rounded-xl" />
                <Skeleton className="mt-4 h-4 w-2/3" />
                <Skeleton className="mt-2 h-3 w-1/3" />
                <Skeleton className="mt-6 h-9 w-full rounded-lg" />
              </Card>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <Card>
            <EmptyState
              icon={FileIcon}
              title={query ? "No files match your search" : "No files yet"}
              description={
                query
                  ? "Try a different name or organisation slug."
                  : "Create a file to start mapping spreadsheets into a Naqood organisation."
              }
              action={
                query ? (
                  <Button variant="secondary" onClick={() => setQuery("")}>
                    Clear search
                  </Button>
                ) : (
                  <Button icon={PlusIcon} onClick={() => setShowAdd(true)}>
                    Create your first file
                  </Button>
                )
              }
            />
          </Card>
        ) : (
          <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
            {filtered.map((file, i) => {
              const connected = file?.naqood?.isConnected;
              return (
                <Card
                  key={file._id}
                  style={{ animationDelay: `${Math.min(i, 8) * 40}ms` }}
                  className={cx(
                    "group animate-slide-up overflow-hidden transition-all duration-200",
                    connected
                      ? "cursor-pointer hover:-translate-y-0.5 hover:border-brand-300 hover:shadow-card-hover"
                      : ""
                  )}
                  onClick={() => connected && navigate(`/file/${file._id}`)}
                >
                  <div className="p-5">
                    <div className="flex items-start justify-between">
                      <span
                        className={cx(
                          "grid h-11 w-11 place-items-center rounded-xl ring-1 transition",
                          connected
                            ? "bg-brand-50 text-brand-600 ring-brand-100"
                            : "bg-ink-100 text-ink-400 ring-ink-200"
                        )}
                      >
                        <FileIcon size={20} />
                      </span>
                    </div>

                    <h3 className="mt-4 truncate text-[15.5px] font-semibold text-ink-900">
                      {file.fileName}
                    </h3>

                    <div className="mt-1 flex items-center gap-1.5 text-[12.5px] text-ink-500">
                      {connected ? (
                        <>
                          <LinkIcon size={12} className="shrink-0" />
                          <span className="truncate font-mono">
                            {file.naqood.organizationSlug || "—"}
                          </span>
                        </>
                      ) : (
                        <span>
                          Created{" "}
                          {new Date(file.createdAt).toLocaleDateString(undefined, {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          })}
                        </span>
                      )}
                    </div>
                  </div>

                  <div
                    className="flex items-center gap-2 border-t border-ink-200/70 bg-ink-50/50 px-5 py-3"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {connected ? (
                      <>
                        <span className="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-emerald-700">
                          <span className="grid h-4 w-4 place-items-center rounded-full bg-emerald-100">
                            <CheckIcon size={10} strokeWidth={3} />
                          </span>
                          Connected
                        </span>

                        <div className="ml-auto flex items-center gap-1">
                          <button
                            onClick={() => deleteFile(file)}
                            title="Delete this file"
                            className="grid h-7 w-7 place-items-center rounded-md text-ink-400 transition hover:bg-red-50 hover:text-red-600"
                            >
                            <TrashIcon size={15} />
                            </button>

                          <button
                            onClick={() => setInfoFile(file)}
                            title="Connection details"
                            className="grid h-7 w-7 place-items-center rounded-md text-ink-500 transition hover:bg-ink-200/60 hover:text-ink-800"
                          >
                            <SettingsIcon size={15} />
                          </button>
                          <ChevronRightIcon
                            size={16}
                            className="text-ink-300 transition group-hover:translate-x-0.5 group-hover:text-brand-500"
                          />
                        </div>
                      </>
                    ) : (
                      <>
                        <Button
                          size="sm"
                          icon={LinkIcon}
                          onClick={() => connectNaqood(file._id)}
                        >
                          Connect
                        </Button>
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => setInfoFile(file)}
                        >
                          Enter keys
                        </Button>
                      </>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </main>

      {showAdd && (
        <AddFileModal onClose={() => setShowAdd(false)} onSave={saveFile} />
      )}

      {infoFile && (
        <NaqoodInfoModal
          file={infoFile}
          onClose={() => setInfoFile(null)}
          onSuccess={(naqoodData) => {
            setFiles((prev) =>
              prev.map((f) =>
                f._id === infoFile._id
                  ? { ...f, naqood: { ...naqoodData, isConnected: true } }
                  : f
              )
            );
          }}
        />
      )}
    </AppShell>
  );
}
